// /client/src/components/DashboardCompta.js
import React, { useState, useEffect, useMemo } from "react";
import CardCompta from "./CardCompta";
import CardComptaNumber from "./CardComptaNumber";
import StatutReglementsDiagramme from "./StatutReglementsDiagramme";
import TypeReglementsDiagramme from "./TypeReglementsDiagramme";
import TableauAchatsGroupes from "./TableauAchatsGroupes";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import { getAllReglementInscriptionBySaison } from "../../../services/reglementinscriptionServices";
import { getSettings } from "../../../services/settingsServices";
import { getReglements } from "../../../services/achatsServices";
import useScrollToTop from "../../../hooks/useScollToTop";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import {
  FaChartPie,
  FaChartBar,
  FaStore,
  FaTimes,
  FaCartPlus,
} from "react-icons/fa";

const CURRENCY = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
});
const isAcompteText = (s = "") =>
  /acompte|arrhes|d[ée]p[ôo]t|deposit|advance/i.test(s);
const seasonBounds = (season) => {
  const [startYear, endYear] = String(season).split("-");
  return {
    start: new Date(`${startYear}-07-01T00:00:00`),
    end: new Date(`${endYear}-06-30T23:59:59`),
  };
};

const TYPES_PAIEMENT_POSSIBLES = [
  "En ligne",
  "Chèque",
  "Espèces",
  "Virement",
  "CB",
  "Autre",
  "Non renseigné",
];

export default function DashboardCompta() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [totalLicences, setTotalLicences] = useState(0);
  const [totalCotisations, setTotalCotisations] = useState(0);
  const [totalReductions, setTotalReductions] = useState(0);
  const [totalGeneral, setTotalGeneral] = useState(0);
  const [totalPassSport, setTotalPassSport] = useState(0);
  const [nombrePassSport, setNombrePassSport] = useState(0);

  const [statutReglements, setStatutReglements] = useState([]);
  const [typeReglements, setTypeReglements] = useState([]);

  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("");
  const [loading, setLoading] = useState(true);

  const [boutiquePreview, setBoutiquePreview] = useState([]);
  const [loadingBoutique, setLoadingBoutique] = useState(true);

  // Modale
  const [modal, setModal] = useState({ open: false, view: null });

  useEffect(() => {
    (async () => {
      try {
        const settings = await getSettings();
        if (settings?.length) {
          const active = settings[0].saison_active;
          setSeasons([prevSeason(active), active, nextSeason(active)]);
          setSelectedSeason(active);
        }
      } catch (e) {
        console.error("Erreur lors du chargement des paramètres:", e);
      }
    })();
  }, []);

  useEffect(() => {
    if (selectedSeason) fetchReglements(selectedSeason);
    fetchBoutiquePreview(selectedSeason);
  }, [selectedSeason]);

  async function fetchReglements(season) {
    try {
      setLoading(true);
      const reglements = await getAllReglementInscriptionBySaison(season);
      if (!Array.isArray(reglements)) return;

      let sumLicences = 0;
      let sumCotisations = 0;
      let sumReductions = 0;
      let nPass = 0;
      let totalPS = 0;
      const statutCounts = {};
      const typeCounts = {};

      for (const r of reglements) {
        sumLicences += parseFloat(r.montant_lic_assurance || 0);
        sumCotisations += parseFloat(r.montant_cotisation || 0);
        sumReductions += parseFloat(
          (r.reduction_foyer || 0) +
            (r.reduction_saison || 0) +
            (r.pass_sport || 0)
        );
        nPass += parseFloat(r.pass_sport || 0) / 50;
        totalPS += parseFloat(r.pass_sport || 0);

        if (r.statut_reglement) {
          statutCounts[r.statut_reglement] =
            (statutCounts[r.statut_reglement] || 0) + 1;
        }
        const type = r.type_reglement?.trim() || "Non renseigné";
        typeCounts[type] = (typeCounts[type] || 0) + 1;
      }

      setTotalLicences(sumLicences);
      setTotalCotisations(sumCotisations);
      setTotalReductions(sumReductions);
      setTotalPassSport(totalPS);
      setTotalGeneral(sumLicences + sumCotisations + sumReductions);
      setNombrePassSport(Math.abs(Math.round(nPass)));

      const total = reglements.length || 1;
      setStatutReglements(
        Object.entries(statutCounts).map(([statut, count]) => ({
          statut,
          percent: Number(((count / total) * 100).toFixed(1)),
          count,
        }))
      );
      setTypeReglements(
        TYPES_PAIEMENT_POSSIBLES.map((type) => {
          const count = typeCounts[type] || 0;
          return {
            type,
            percent: Number(((count / total) * 100).toFixed(1)),
            count,
          };
        })
      );
    } catch (e) {
      console.error("Erreur fetchReglements:", e);
    } finally {
      setLoading(false);
    }
  }

  const topStatuts = useMemo(
    () =>
      [...statutReglements].sort((a, b) => b.percent - a.percent).slice(0, 3),
    [statutReglements]
  );
  const topTypes = useMemo(
    () => [...typeReglements].sort((a, b) => b.percent - a.percent).slice(0, 3),
    [typeReglements]
  );

  const openModal = (view) => setModal({ open: true, view });
  const closeModal = () => setModal({ open: false, view: null });

  async function fetchBoutiquePreview(season) {
    try {
      setLoadingBoutique(true);
      const { start, end } = seasonBounds(season);
      const achats = await getReglements();
      if (!Array.isArray(achats)) {
        setBoutiquePreview([]);
        return;
      }

      // Filtre: uniquement lignes boutique avec Article renseigné, dans la saison, sans “Acompte”
      const filtered = achats.filter((a) => {
        if (!a?.Article) return false; // “boutique” vs règlements d’inscription (Article null)
        const d = new Date(a.date_achat);
        if (!(d >= start && d <= end)) return false;

        const name = a.Article?.nom || "";
        const desc = a.Article?.description || "";
        const msg = a.message_achat || "";
        if (isAcompteText(name) || isAcompteText(desc) || /acompte/i.test(msg))
          return false;

        // (optionnel) n’afficher que type_achat === "boutique"
        // if (a.type_achat && a.type_achat !== "boutique") return false;

        return true;
      });

      // Agrégation CA par article
      const map = new Map();
      for (const a of filtered) {
        const key = a.Article?.nom || "Article";
        const ca = Number.parseFloat(a.prix_total || 0);
        map.set(key, (map.get(key) || 0) + ca);
      }

      const arr = [...map.entries()]
        .map(([article, ca]) => ({ article, ca }))
        .sort((a, b) => b.ca - a.ca);

      const totalCA = arr.reduce((s, x) => s + x.ca, 0) || 1;

      // On prépare le TOP 3 pour la carte (label + %)
      const items = arr.slice(0, 3).map((x) => ({
        label: `${x.article} — ${CURRENCY.format(x.ca)}`,
        percent: Number(((x.ca / totalCA) * 100).toFixed(1)),
      }));

      setBoutiquePreview(items);
    } catch (e) {
      console.error("Erreur fetchBoutiquePreview:", e);
      setBoutiquePreview([]);
    } finally {
      setLoadingBoutique(false);
    }
  }

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header */}
        <HeaderTitle
          role="Administrateur"
          title="Statistiques Comptabilité"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Bandeau dégradé */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Règlements d’inscriptions, types de paiements & achats boutique
              </h2>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                Choisissez une saison et explorez les indicateurs. Cliquez sur
                un module pour l’ouvrir en grand.
              </p>
            </div>
          </div>

          {/* Sélecteur saison */}
          <div className="px-5 pt-5 sm:px-8">
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Sélectionner la saison
            </label>
            <select
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
            >
              {seasons.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {/* KPI Cards */}
          <div className="px-5 pb-2 sm:px-8">
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
              <CardCompta
                title="Total Licences"
                value={totalLicences}
                color="blue"
              />
              <CardCompta
                title="Total Cotisations - Réductions"
                value={totalCotisations}
                color="yellow"
              />
              <CardCompta
                title="Total Réductions"
                value={totalReductions}
                color="red"
              />
              <CardCompta
                title="Total Règlements"
                value={totalGeneral}
                color="green"
              />
              <CardCompta
                title="Total Pass'Sport"
                value={totalPassSport}
                color="purple"
              />
              <CardComptaNumber
                title="Nombre de Pass'Sport"
                value={nombrePassSport}
                color="black"
              />
            </div>
          </div>

          {/* Miniatures modules */}
          <div className="px-5 pb-6 sm:px-8">
            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Statuts de règlements */}
              <PreviewCard
                title="Répartition des statuts"
                Icon={FaChartPie}
                loading={loading}
                items={topStatuts.map((s) => ({
                  label: s.statut,
                  percent: s.percent,
                }))}
                onOpen={() => openModal("statuts")}
              />

              {/* Types de règlements */}
              <PreviewCard
                title="Répartition des types de paiement"
                Icon={FaChartPie}
                loading={loading}
                items={topTypes.map((t) => ({
                  label: t.type,
                  percent: t.percent,
                }))}
                onOpen={() => openModal("types")}
              />

              {/* Achats boutique */}
              <PreviewCard
                title="Achats boutique (par article/groupe)"
                Icon={FaCartPlus}
                loading={loadingBoutique}
                items={boutiquePreview} // ⬅️ on alimente la barre de progression
                hint="Répartition des ventes par article (hors acomptes)"
                onOpen={() => openModal("achats")}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modale */}
      {modal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={closeModal}
            aria-hidden
          />
          {/* Conteneur */}
          <div className="relative w-full max-w-5xl max-h-[85vh] overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
            {/* Header gradient */}
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 flex items-center justify-between">
              <h3 className="text-white text-lg sm:text-xl font-bold uppercase">
                {modal.view === "statuts" && "Statuts de règlements"}
                {modal.view === "types" && "Types de règlements"}
                {modal.view === "achats" && "Achats boutique"}
              </h3>
              <button
                onClick={closeModal}
                className="inline-flex items-center justify-center rounded-xl bg-white/90 text-slate-900 hover:bg-white w-9 h-9"
                title="Fermer"
              >
                <FaTimes />
              </button>
            </div>

            {/* Corps modale scrollable */}
            <div className="p-5 sm:p-6 overflow-auto max-h-[calc(85vh-70px)]">
              {modal.view === "statuts" && (
                <div className="w-full max-w-4xl mx-auto">
                  <StatutReglementsDiagramme data={statutReglements} />
                </div>
              )}
              {modal.view === "types" && (
                <div className="w-full max-w-4xl mx-auto">
                  <TypeReglementsDiagramme data={typeReglements} />
                </div>
              )}
              {modal.view === "achats" && (
                <div className="w-full max-w-5xl mx-auto">
                  <TableauAchatsGroupes selectedSeason={selectedSeason} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ----------- Helpers / mini-composants ----------- */

function prevSeason(s) {
  const [a, b] = (s || "").split("-").map(Number);
  return `${a - 1}-${b - 1}`;
}
function nextSeason(s) {
  const [a, b] = (s || "").split("-").map(Number);
  return `${a + 1}-${b + 1}`;
}

function PreviewCard({ title, Icon, loading, items = [], hint, onOpen }) {
  return (
    <div className="rounded-2xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white/90 dark:bg-slate-900/60 backdrop-blur p-4 flex flex-col">
      <div className="flex items-center gap-2">
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800">
          <Icon className="text-slate-700 dark:text-slate-200" />
        </div>
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {title}
        </div>
      </div>

      <div className="mt-3">
        {loading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-4 w-full rounded bg-slate-200 dark:bg-slate-800 animate-pulse"
              />
            ))}
          </div>
        ) : items.length ? (
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.label} className="text-sm">
                <div className="flex justify-between text-slate-700 dark:text-slate-300">
                  <span className="truncate">{it.label}</span>
                  <span className="ml-2 tabular-nums">{it.percent}%</span>
                </div>
                <div className="mt-1 h-2 rounded bg-slate-200 dark:bg-slate-800 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-violet-400 to-pink-400"
                    style={{
                      width: `${Math.max(0, Math.min(100, it.percent))}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Aucune donnée à afficher.
          </div>
        )}
        {hint && (
          <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {hint}
          </div>
        )}
      </div>

      <div className="mt-4">
        <button
          onClick={onOpen}
          className="inline-flex items-center justify-center rounded-xl px-3 py-2 text-sm font-medium bg-white text-slate-900 hover:bg-slate-50 shadow ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:ring-slate-700"
        >
          Voir les détails
        </button>
      </div>
    </div>
  );
}
